/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import {
    CashBoxDocumentsTabAction,
    CashBoxDocumentsTabActionType,
} from '../../../actions/CashBoxRegistry/Tabs/cashBoxDocumentsTabActions';
import { CashBoxDocumentsTabFilterParams, DocumentsListItemDto } from '../../../api/documents/documentsClient.typings';
import { today } from '../cashboxDetailsReducer';
import moment from 'moment';
import { DATE_TIME_WITH_MS_FORMAT } from '../../../../utils/utils';

export const defaultDocsSearchType = 'cashBoxTaxNumber';
export const defaultDocsLimit = 10;

export interface CashBoxDocumentsTabState {
    cashBoxDocumentsList: DocumentsListItemDto[];
    isLoadingDocsList: boolean;
    isDownloadingDocuments: boolean;
    docsFilterParams: CashBoxDocumentsTabFilterParams;
    docsLimit: number;
    docsAfterKeyCreatedAtUtc: string;
    docsAfterKeyId: string;
    animateAfterDocDate: string;
    newDocsInfo: {
        count: number;
        newDocs?: DocumentsListItemDto[];
        afterDate?: string;
    };
    lastRequestDocumentsCount: number;
}
const initialState: CashBoxDocumentsTabState = {
    cashBoxDocumentsList: [],
    docsFilterParams: {
        search_type: defaultDocsSearchType,
        search_text: '',
        dateFrom: null,
        dateTo: null,
        docTypeIds: [],
    },
    docsLimit: defaultDocsLimit,
    docsAfterKeyCreatedAtUtc: null,
    docsAfterKeyId: null,
    isLoadingDocsList: false,
    animateAfterDocDate: null,
    newDocsInfo: {
        count: 0,
        newDocs: [],
        afterDate: null,
    },
    lastRequestDocumentsCount: null,
    isDownloadingDocuments: false,
};

export function cashBoxDocumentsTabReducer(
    state: CashBoxDocumentsTabState = initialState,
    action: CashBoxDocumentsTabAction,
): CashBoxDocumentsTabState {
    switch (action.type) {
        case CashBoxDocumentsTabActionType.OPEN_CASHBOX_DOCUMENTS_TAB:
            return {
                ...state,
                isLoadingDocsList: true,
                docsFilterParams: initialState.docsFilterParams,
            };

        case CashBoxDocumentsTabActionType.FETCH_CASHBOX_DOCUMENTS_SUCCESS:
            const combinedList: DocumentsListItemDto[] = [...state.cashBoxDocumentsList];

            if (action.payload.documents && action.payload.documents.length > 0) {
                combinedList.push(...action.payload.documents);
            }

            return {
                ...state,
                isLoadingDocsList: false,
                cashBoxDocumentsList: combinedList,
                docsAfterKeyCreatedAtUtc: action.payload.afterKeyCreatedAtUtc,
                docsAfterKeyId: action.payload.afterKeyId,
                lastRequestDocumentsCount: action.payload.documents.length,
                newDocsInfo: {
                    ...state.newDocsInfo,
                    afterDate:
                        combinedList.length !== 0
                            ? combinedList[0].createdAtUtc
                            : today.utc().clone().subtract(0, 'day').format(),
                },
            };

        case CashBoxDocumentsTabActionType.FETCH_CASHBOX_DOCUMENTS_ERROR:
            return {
                ...state,
                isLoadingDocsList: false,
            };

        case CashBoxDocumentsTabActionType.APPLY_DOCUMENTS_FILTERING:
            return {
                ...state,
                isLoadingDocsList: true,
                docsFilterParams: action.payload,
                cashBoxDocumentsList: [],
                docsAfterKeyCreatedAtUtc: null,
                docsAfterKeyId: null,
                lastRequestDocumentsCount: null,
            };

        case CashBoxDocumentsTabActionType.APPLY_DOCUMENTS_PAGING:
            return {
                ...state,
                isLoadingDocsList: true,
            };

        case CashBoxDocumentsTabActionType.CHECK_NEW_DOCUMENTS_SUCCESS:
            return {
                ...state,
                newDocsInfo: {
                    ...state.newDocsInfo,
                    count: action.payload.documents.length,
                    newDocs: [...action.payload.documents],
                },
                animateAfterDocDate: null,
            };

        case CashBoxDocumentsTabActionType.SHOW_NEW_DOCUMENTS:
            const newDocs = state.newDocsInfo.newDocs;
            const newList: DocumentsListItemDto[] = [...newDocs, ...state.cashBoxDocumentsList];

            return {
                ...state,
                cashBoxDocumentsList: newList,
                newDocsInfo: {
                    count: 0,
                    newDocs: [],
                    afterDate:
                        newList.length !== 0
                            ? newList[0].createdAtUtc
                            : today.utc().clone().subtract(0, 'day').format(DATE_TIME_WITH_MS_FORMAT),
                },
                animateAfterDocDate:
                    newDocs.length !== 0
                        ? moment(newDocs[newDocs.length - 1].createdAtUtc)
                              .utc()
                              .add(-1, 'milliseconds')
                              .format(DATE_TIME_WITH_MS_FORMAT)
                        : null,
            };

        case CashBoxDocumentsTabActionType.DOWNLOAD_DOCUMENTS:
            return {
                ...state,
                isDownloadingDocuments: true,
            };

        case CashBoxDocumentsTabActionType.DOWNLOAD_DOCUMENTS_SUCCESS:
        case CashBoxDocumentsTabActionType.DOWNLOAD_DOCUMENTS_ERROR:
            return {
                ...state,
                isDownloadingDocuments: false,
            };

        case CashBoxDocumentsTabActionType.EXIT_CASHBOX_DOCUMENTS_TAB:
            return {
                ...state,
                cashBoxDocumentsList: [],
                docsAfterKeyCreatedAtUtc: null,
                docsAfterKeyId: null,
                lastRequestDocumentsCount: null,
                animateAfterDocDate: null,
                newDocsInfo: initialState.newDocsInfo,
            };

        default:
            break;
    }
    return state;
}
